/**
 * BigwaToast — Componente reutilizável para toasts com botões de ação.
 *
 * Substitui os 4+ padrões inline de JSX em toasts do app.
 * Estilização "quiet luxury": fundo escuro, borda esquerda semântica,
 * botões claramente visíveis e clicáveis.
 */
import React from 'react';
import toast from 'react-hot-toast';
import type { Toast } from 'react-hot-toast';
import './BigwaToast.css';

const VARIANT_BORDER: Record<string, string> = {
  success: '#477B72',
  error: '#D4918A',
  warning: '#F2BC41',
};

/**
 * Intenções de botão (Guia de Cores Bigwa):
 * - 'primary'     → Verde Olho #477B72 (Confirmar, Restaurar, Classificar)
 * - 'alert'       → Amarelo Bico #F2BC41 (Desfazer, Reverter, Atenção)
 * - 'destructive' → Coral #D4918A (Deletar, Excluir, Remover)
 * - 'neutral'     → Cinza #DADADA (Cancelar, Voltar, Fechar)
 * - 'informative' → Azul #A8C4D4 (Exportar, Importar, Visualizar)
 */
export type ButtonIntent = 'primary' | 'alert' | 'destructive' | 'neutral' | 'informative';

export interface ToastAction {
  label: string;
  onClick: () => void;
  intent?: ButtonIntent;
}

export interface BigwaToastProps {
  t: Toast;
  message: string;
  description?: string;
  primaryAction?: ToastAction;
  secondaryAction?: ToastAction;
  variant?: 'default' | 'success' | 'error' | 'warning';
}

export const BigwaToast: React.FC<BigwaToastProps> = ({
  t,
  message,
  description,
  primaryAction,
  secondaryAction,
  variant = 'default',
}) => {
  const borderColor = VARIANT_BORDER[variant];
  const containerStyle: React.CSSProperties = borderColor
    ? { borderLeft: `3px solid ${borderColor}` }
    : {};

  const handlePrimary = () => {
    primaryAction?.onClick();
    toast.dismiss(t.id);
  };

  const handleSecondary = () => {
    secondaryAction?.onClick();
    toast.dismiss(t.id);
  };

  return (
    <div
      className={`bigwa-toast ${t.visible ? 'bigwa-toast--visible' : 'bigwa-toast--hidden'}`}
      style={containerStyle}
    >
      <div className="bigwa-toast__content">
        <span className="bigwa-toast__message">{message}</span>
        {description && (
          <span className="bigwa-toast__description">{description}</span>
        )}
      </div>
      {(primaryAction || secondaryAction) && (
        <div className="bigwa-toast__actions">
          {secondaryAction && (
            <button
              className={`bigwa-toast__btn bigwa-toast__btn--${secondaryAction.intent || 'neutral'}`}
              onClick={handleSecondary}
            >
              {secondaryAction.label}
            </button>
          )}
          {primaryAction && (
            <button
              className={`bigwa-toast__btn bigwa-toast__btn--${primaryAction.intent || 'primary'}`}
              onClick={handlePrimary}
            >
              {primaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
